import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  type Dispatch,
  type KeyboardEvent as ReactKeyboardEvent,
  type SetStateAction,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/router/paths';

interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: string;
  group: string;
  run: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export default function CommandPalette({ open, setOpen }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();

  const commands = useMemo<Command[]>(
    () => [
      {
        id: 'go-dynamics',
        group: 'Аналитика',
        label: 'Динамика',
        hint: 'Динамика позиций и сравнение запусков',
        icon: 'ri-line-chart-line',
        run: () => navigate(ROUTES.analytics.dynamics),
      },
      {
        id: 'go-wordstat-reports',
        group: 'Аналитика',
        label: 'Отчёты Wordstat',
        icon: 'ri-bar-chart-grouped-line',
        run: () => navigate(ROUTES.analytics.wordstat),
      },
      {
        id: 'go-wordstat-tasks',
        group: 'Задание',
        label: 'Задания Wordstat',
        icon: 'ri-bar-chart-grouped-line',
        run: () => navigate(ROUTES.tasks.wordstat),
      },
      {
        id: 'new-wordstat-task',
        group: 'Действия',
        label: 'Новое задание Wordstat',
        icon: 'ri-add-line',
        run: () => navigate(ROUTES.tasks.wordstatNew),
      },
      {
        id: 'go-serpset',
        group: 'Аналитика',
        label: 'Выдача',
        hint: 'Просмотр и сравнение сохранённой SERP',
        icon: 'ri-search-eye-line',
        run: () => navigate(ROUTES.analytics.serpset),
      },
      {
        id: 'go-dashboard',
        group: 'Задание',
        label: 'Обзор',
        hint: 'Сводка по заданию',
        icon: 'ri-pulse-line',
        run: () => navigate(ROUTES.tasks.overview),
      },
      {
        id: 'go-crons',
        group: 'Задание',
        label: 'Регулярные',
        hint: 'Автозапуски по cron',
        icon: 'ri-time-line',
        run: () => navigate(ROUTES.tasks.recurring),
      },
      {
        id: 'go-experiments',
        group: 'Задание',
        label: 'Единоразовые',
        hint: 'Разовые прокачки выдачи',
        icon: 'ri-flask-line',
        run: () => navigate(ROUTES.tasks.oneTime),
      },
      {
        id: 'go-targets',
        group: 'Задание',
        label: 'Цели',
        hint: 'Сохранённые правила поиска URL',
        icon: 'ri-focus-3-line',
        run: () => navigate(ROUTES.tasks.targets),
      },
      {
        id: 'go-baskets',
        group: 'Задание',
        label: 'Корзины',
        hint: 'Списки поисковых запросов',
        icon: 'ri-archive-line',
        run: () => navigate(ROUTES.tasks.baskets),
      },
      {
        id: 'go-credentials',
        group: 'Задание',
        label: 'Подключения',
        hint: 'Учётные данные Yandex Search API',
        icon: 'ri-key-2-line',
        run: () => navigate(ROUTES.tasks.connections),
      },
      {
        id: 'new-basket',
        group: 'Действия',
        label: 'Создать корзину',
        hint: 'Новый список запросов',
        icon: 'ri-add-line',
        run: () => navigate(ROUTES.tasks.baskets),
      },
      {
        id: 'new-experiment',
        group: 'Действия',
        label: 'Создать эксперимент',
        hint: 'Мастер запуска прокачки',
        icon: 'ri-flask-line',
        run: () => navigate(ROUTES.tasks.oneTimeNew),
      },
      {
        id: 'new-cron',
        group: 'Действия',
        label: 'Создать расписание',
        hint: 'Настроить автозапуск',
        icon: 'ri-time-line',
        run: () => navigate(ROUTES.tasks.recurringNew),
      },
    ],
    [navigate],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.group.toLowerCase().includes(q) ||
        (c.hint && c.hint.toLowerCase().includes(q)),
    );
  }, [query, commands]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setOpen]);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;

    const activeElement = document.activeElement;
    returnFocusRef.current =
      activeElement instanceof HTMLElement && activeElement !== document.body
        ? activeElement
        : null;

    if (!dialog.open) dialog.showModal();
    setQuery('');
    setActiveIndex(0);
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 0);

    return () => {
      clearTimeout(focusTimer);
      if (dialog.open) dialog.close();
      const returnTarget = returnFocusRef.current;
      if (returnTarget?.isConnected) returnTarget.focus();
      returnFocusRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const runCommand = (cmd: Command) => {
    setOpen(false);
    cmd.run();
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIndex]) runCommand(filtered[activeIndex]);
    }
  };

  if (!open) return null;

  const groups = filtered.reduce<Record<string, Command[]>>((acc, c) => {
    (acc[c.group] ||= []).push(c);
    return acc;
  }, {});

  return (
    <dialog
      ref={dialogRef}
      aria-label="Быстрый переход"
      className="m-0 h-dvh max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-black/40 backdrop:backdrop-blur-[2px]"
      onCancel={(event) => {
        event.preventDefault();
        setOpen(false);
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) setOpen(false);
      }}
    >
      <div className="raised-panel mx-auto mt-[12vh] w-[calc(100%-2rem)] max-w-xl overflow-hidden rounded-lg border border-surface-200">
        <div className="flex items-center gap-3 px-4 border-b border-surface-200">
          <i className="ri-search-line text-surface-400" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Поиск по разделам и действиям..."
            aria-label="Поиск по разделам и действиям"
            aria-controls="command-palette-results"
            aria-expanded="true"
            aria-activedescendant={
              filtered[activeIndex] ? `command-${filtered[activeIndex].id}` : undefined
            }
            role="combobox"
            className="flex-1 py-4 text-sm text-surface-900 placeholder-surface-400 focus:outline-none bg-transparent"
          />
          <kbd className="rounded border border-surface-200 bg-surface-100 px-1.5 py-0.5 font-mono text-[11px] text-surface-500">
            ESC
          </kbd>
        </div>

        <div
          id="command-palette-results"
          className="max-h-[50vh] overflow-y-auto py-2"
          role="listbox"
        >
          {filtered.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-surface-400">Ничего не найдено</div>
          )}

          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              <div className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-surface-400">
                {group}
              </div>
              {items.map((cmd) => {
                const idx = filtered.indexOf(cmd);
                const active = idx === activeIndex;
                return (
                  <button
                    key={cmd.id}
                    id={`command-${cmd.id}`}
                    onClick={() => runCommand(cmd)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      active ? 'bg-accent-50' : ''
                    }`}
                    role="option"
                    aria-selected={active}
                  >
                    <span
                      className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                        active ? 'bg-accent-500 text-white' : 'bg-surface-100 text-surface-500'
                      }`}
                    >
                      <i className={`${cmd.icon} text-sm`} aria-hidden="true" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-surface-900">
                        {cmd.label}
                      </span>
                      {cmd.hint && (
                        <span className="block text-xs text-surface-400 truncate">{cmd.hint}</span>
                      )}
                    </span>
                    {active && (
                      <i className="ri-corner-down-left-line text-surface-400" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </dialog>
  );
}
